import { useState, useEffect } from 'react';
import RichMenuEditor from '../components/RichMenuEditor';
import {
  Box,
  Card,
  Typography,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save,
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Refresh,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bot-tabpanel-${index}`}
      aria-labelledby={`bot-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface BotSettings {
  _id?: string;
  // LINE Credentials
  line_channel_id?: string;
  line_channel_secret?: string;
  line_channel_access_token?: string;
  line_login_channel_id?: string;
  line_login_channel_secret?: string;
  liff_id?: string;
  // Telegram Integration
  telegram_bot_token?: string;
  telegram_admin_chat_id?: string;
  telegram_alerts_enabled?: boolean;
  // Bot Settings
  bot_name: string;
  welcome_message: string;
  fallback_message: string;
  auto_reply_enabled: boolean;
  flex_messages_enabled: boolean;
  behavior_tracking_enabled: boolean;
  rich_menu_id?: string;
  webhook_enabled: boolean;
  webhook_url?: string;
  line_login_callback_url?: string;
  admin_line_ids: string[];
}

interface ReplyRule {
  _id: string;
  name: string;
  description?: string;
  keywords: string[];
  match_type: string;
  pattern?: string;
  reply_type: string;
  reply_content: string;
  priority: number;
  is_active: boolean;
  usage_count: number;
}

interface FlexTemplate {
  _id: string;
  name: string;
  description?: string;
  category: string;
  flex_content: any;
  variables: string[];
  is_active: boolean;
  usage_count: number;
}

interface LiffApp {
  _id: string;
  liff_id: string;
  app_name: string;
  app_key: string;
  description?: string;
  endpoint_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface RichMenu {
  _id: string;
  rich_menu_id?: string;
  name: string;
  alias?: string;
  chat_bar_text: string;
  selected: boolean;
  size: { width: number; height: number };
  areas: Array<{
    bounds: { x: number; y: number; width: number; height: number };
    action: { type: string; label?: string; text?: string; uri?: string; data?: string };
  }>;
  image_url?: string;
  description?: string;
  is_template: boolean;
  created_at: string;
  updated_at: string;
}

interface BroadcastForm {
  message_type: 'text' | 'flex';
  text: string;
  flex_content: string;
}

interface FollowerStats {
  status: string;
  targetReaches: number;
  followers: number;
  date: string;
}

function LineBotManagement() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({
    channelSecret: false,
    accessToken: false,
    loginSecret: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });

  // Bot Settings State
  const [settings, setSettings] = useState<BotSettings>({
    line_channel_id: '',
    line_channel_secret: '',
    line_channel_access_token: '',
    line_login_channel_id: '',
    line_login_channel_secret: '',
    liff_id: '',
    bot_name: 'SRU Alumni Bot',
    welcome_message: 'Welcome to SRU Alumni System!',
    fallback_message: "Sorry, I didn't understand that.",
    auto_reply_enabled: true,
    flex_messages_enabled: true,
    behavior_tracking_enabled: true,
    webhook_enabled: true,
    webhook_url: '',
    line_login_callback_url: '',
    admin_line_ids: [],
  });

  // Reply Rules State
  const [replyRules, setReplyRules] = useState<ReplyRule[]>([]);
  const [replyRuleDialog, setReplyRuleDialog] = useState({
    open: false,
    mode: 'create' as 'create' | 'edit',
    data: null as ReplyRule | null,
  });
  const [replyRuleForm, setReplyRuleForm] = useState({
    name: '',
    description: '',
    keywords: '',
    match_type: 'contains',
    pattern: '',
    reply_type: 'text',
    reply_content: '',
    priority: 0,
    is_active: true,
  });

  // Flex Templates State
  const [flexTemplates, setFlexTemplates] = useState<FlexTemplate[]>([]);
  const [flexTemplateDialog, setFlexTemplateDialog] = useState({
    open: false,
    mode: 'create' as 'create' | 'edit',
    data: null as FlexTemplate | null,
  });
  const [flexTemplateForm, setFlexTemplateForm] = useState({
    name: '',
    description: '',
    category: 'general',
    flex_content: '',
    variables: '',
    is_active: true,
  });

  // LIFF Apps State
  const [liffApps, setLiffApps] = useState<LiffApp[]>([]);
  const [liffDialog, setLiffDialog] = useState({
    open: false,
    mode: 'create' as 'create' | 'edit',
    data: null as LiffApp | null,
  });
  const [liffForm, setLiffForm] = useState({
    liff_id: '',
    app_name: '',
    app_key: '',
    description: '',
    endpoint_url: '',
    is_active: true,
  });

  // Broadcast Messages State
  const [broadcastDialog, setBroadcastDialog] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState<BroadcastForm>({
    message_type: 'text',
    text: '',
    flex_content: '',
  });
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  // User Management State
  const [followerStats, setFollowerStats] = useState<FollowerStats | null>(null);
  const [loadingFollowers, setLoadingFollowers] = useState(false);

  // Rich Menu State
  const [richMenus, setRichMenus] = useState<RichMenu[]>([]);
  const [richMenuDialog, setRichMenuDialog] = useState({
    open: false,
    mode: 'create' as 'create' | 'edit',
    data: null as RichMenu | null,
  });
  const [loadingRichMenus, setLoadingRichMenus] = useState(false);

  // API Status State
  const [apiStatus, setApiStatus] = useState<{
    profile: any | null;
    quota: any | null;
    loading: boolean;
    connectionStatus: 'connected' | 'disconnected' | 'checking' | null;
  }>({
    profile: null,
    quota: null,
    loading: false,
    connectionStatus: null,
  });

  useEffect(() => {
    fetchBotSettings();
    fetchReplyRules();
    fetchFlexTemplates();
    fetchLiffApps();
    fetchFollowerStats();
    fetchRichMenus();
  }, []);

  const fetchBotSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/v1/line/bot/settings`);
      setSettings(response.data);
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to load bot settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchReplyRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/line/reply-rules`);
      setReplyRules(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to load reply rules:', error);
    }
  };

  const fetchFlexTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/line/flex-templates`);
      setFlexTemplates(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to load flex templates:', error);
    }
  };

  const fetchLiffApps = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/liff/liff-apps`);
      setLiffApps(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to load LIFF apps:', error);
    }
  };

  const fetchFollowerStats = async () => {
    try {
      setLoadingFollowers(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/line/followers/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'ready' && response.data.followers) {
        setFollowerStats(response.data.followers);
      }
    } catch (error: any) {
      console.error('Failed to load follower stats:', error);
    } finally {
      setLoadingFollowers(false);
    }
  };

  const fetchRichMenus = async () => {
    try {
      setLoadingRichMenus(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/rich-menus`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Rich Menus Response:', response.data);
      const menus = response.data.rich_menus || [];
      console.log('Setting Rich Menus:', menus);
      setRichMenus(menus);
    } catch (error: any) {
      console.error('Failed to load rich menus:', error);
      showSnackbar(error.response?.data?.detail || 'Failed to load rich menus', 'error');
    } finally {
      setLoadingRichMenus(false);
    }
  };

  const handleSendBroadcast = async () => {
    try {
      setSendingBroadcast(true);
      const token = localStorage.getItem('token');
      
      const payload: any = {
        message_type: broadcastForm.message_type,
      };

      if (broadcastForm.message_type === 'text') {
        payload.text = broadcastForm.text;
      } else {
        try {
          payload.flex_content = JSON.parse(broadcastForm.flex_content);
        } catch (e) {
          showSnackbar('Invalid JSON format for Flex Message', 'error');
          return;
        }
      }

      const response = await axios.post(
        `${API_URL}/api/v1/line/broadcast`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'success') {
        showSnackbar('Broadcast message sent successfully', 'success');
        setBroadcastDialog(false);
        setBroadcastForm({ message_type: 'text', text: '', flex_content: '' });
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to send broadcast', 'error');
    } finally {
      setSendingBroadcast(false);
    }
  };

  const handlePublishRichMenu = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v1/rich-menus/${id}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'success') {
        showSnackbar('Rich menu published to LINE successfully', 'success');
        fetchRichMenus();
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to publish rich menu', 'error');
    }
  };

  const handleSetDefaultRichMenu = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v1/rich-menus/${id}/set-default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'success') {
        showSnackbar('Default rich menu set successfully', 'success');
        fetchRichMenus();
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to set default rich menu', 'error');
    }
  };

  const handleDeleteRichMenu = async (id: string) => {
    if (!confirm('Are you sure you want to delete this rich menu?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/v1/rich-menus/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar('Rich menu deleted successfully', 'success');
      fetchRichMenus();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to delete rich menu', 'error');
    }
  };

  const fetchBotProfile = async () => {
    try {
      setApiStatus(prev => ({ ...prev, loading: true, connectionStatus: 'checking' }));
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/line/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'connected') {
        setApiStatus(prev => ({
          ...prev,
          profile: response.data.profile,
          connectionStatus: 'connected',
          loading: false
        }));
        showSnackbar('Bot profile fetched successfully', 'success');
      } else {
        setApiStatus(prev => ({
          ...prev,
          connectionStatus: 'disconnected',
          loading: false
        }));
        showSnackbar(response.data.message || 'Failed to fetch bot profile', 'error');
      }
    } catch (error: any) {
      setApiStatus(prev => ({
        ...prev,
        connectionStatus: 'disconnected',
        loading: false
      }));
      showSnackbar(error.response?.data?.detail || 'Failed to fetch bot profile', 'error');
    }
  };

  const fetchMessageQuota = async () => {
    try {
      setApiStatus(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/line/quota`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'success') {
        setApiStatus(prev => ({
          ...prev,
          quota: response.data,
          loading: false
        }));
        showSnackbar('Message quota fetched successfully', 'success');
      } else {
        showSnackbar(response.data.message || 'Failed to fetch quota', 'error');
        setApiStatus(prev => ({ ...prev, loading: false }));
      }
    } catch (error: any) {
      setApiStatus(prev => ({ ...prev, loading: false }));
      showSnackbar(error.response?.data?.detail || 'Failed to fetch quota', 'error');
    }
  };

  const configureWebhook = async () => {
    try {
      const webhookUrl = settings.webhook_url || `${API_URL}/api/v1/line/webhook`;
      
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v1/line/webhook/configure`,
        null,
        {
          params: { webhook_url: webhookUrl },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.status === 'success') {
        showSnackbar('Webhook configured successfully', 'success');
      } else {
        showSnackbar(response.data.message || 'Failed to configure webhook', 'error');
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to configure webhook', 'error');
    } finally {
      setSaving(false);
    }
  };

  const configureLoginCallback = async () => {
    try {
      const token = localStorage.getItem('token');
      const callbackUrl = `${API_URL}/api/v1/line/auth/callback`;
      const response = await axios.post(
        `${API_URL}/api/v1/line/login-callback/configure`,
        null,
        {
          params: { callback_url: callbackUrl },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      showSnackbar(response.data.message || 'Check LINE Developers Console to configure callback', 'info');
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed', 'error');
    }
  };

  const fetchWebhookInfo = async () => {
    try {
      setApiStatus(prev => ({ ...prev, loading: true }));
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/v1/line/webhook/info`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.status === 'success') {
        const webhookData = response.data.webhook;
        const localSettings = response.data.local_settings;
        
        // Update settings with fetched data
        setSettings(prev => ({
          ...prev,
          webhook_url: webhookData.endpoint || localSettings.webhook_url,
          webhook_enabled: webhookData.active !== undefined ? webhookData.active : localSettings.webhook_enabled,
          line_login_callback_url: localSettings.line_login_callback_url
        }));
        
        showSnackbar('Webhook info fetched successfully', 'success');
      } else {
        showSnackbar(response.data.message || 'Failed to fetch webhook info', 'error');
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to fetch webhook info', 'error');
    } finally {
      setApiStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/v1/line/bot/settings`,
        settings,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      // Check if webhook was auto-configured
      if (response.data.webhook_config_result) {
        const webhookResult = response.data.webhook_config_result;
        if (webhookResult.success) {
          showSnackbar(`Settings saved! ${webhookResult.message}`, 'success');
        } else {
          showSnackbar(`Settings saved, but ${webhookResult.message}`, 'info');
        }
      } else {
        showSnackbar('Bot settings saved successfully', 'success');
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!confirm('Are you sure you want to delete this reply rule?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/v1/line/reply-rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSnackbar('Reply rule deleted successfully', 'success');
      fetchReplyRules();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to delete reply rule', 'error');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this flex template?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/v1/line/flex-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSnackbar('Flex template deleted successfully', 'success');
      fetchFlexTemplates();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to delete template', 'error');
    }
  };

  // Reply Rule Dialog Handlers
  const handleOpenReplyRuleDialog = (mode: 'create' | 'edit', rule?: ReplyRule) => {
    if (mode === 'edit' && rule) {
      setReplyRuleForm({
        name: rule.name,
        description: rule.description || '',
        keywords: rule.keywords.join(', '),
        match_type: rule.match_type,
        pattern: rule.pattern || '',
        reply_type: rule.reply_type,
        reply_content: typeof rule.reply_content === 'string' ? rule.reply_content : JSON.stringify(rule.reply_content, null, 2),
        priority: rule.priority,
        is_active: rule.is_active,
      });
      setReplyRuleDialog({ open: true, mode, data: rule });
    } else {
      setReplyRuleForm({
        name: '',
        description: '',
        keywords: '',
        match_type: 'contains',
        pattern: '',
        reply_type: 'text',
        reply_content: '',
        priority: 0,
        is_active: true,
      });
      setReplyRuleDialog({ open: true, mode, data: null });
    }
  };

  const handleCloseReplyRuleDialog = () => {
    setReplyRuleDialog({ open: false, mode: 'create', data: null });
    setReplyRuleForm({
      name: '',
      description: '',
      keywords: '',
      match_type: 'contains',
      pattern: '',
      reply_type: 'text',
      reply_content: '',
      priority: 0,
      is_active: true,
    });
  };

  const handleSaveReplyRule = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: replyRuleForm.name,
        description: replyRuleForm.description || undefined,
        keywords: replyRuleForm.keywords.split(',').map(k => k.trim()).filter(k => k),
        match_type: replyRuleForm.match_type,
        pattern: replyRuleForm.pattern || undefined,
        reply_type: replyRuleForm.reply_type,
        reply_content: replyRuleForm.reply_type === 'flex' ? JSON.parse(replyRuleForm.reply_content) : replyRuleForm.reply_content,
        priority: replyRuleForm.priority,
        is_active: replyRuleForm.is_active,
      };
      
      if (replyRuleDialog.mode === 'create') {
        await axios.post(`${API_URL}/api/v1/line/reply-rules`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar('Reply rule created successfully', 'success');
      } else if (replyRuleDialog.data) {
        await axios.put(`${API_URL}/api/v1/line/reply-rules/${replyRuleDialog.data._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar('Reply rule updated successfully', 'success');
      }
      
      handleCloseReplyRuleDialog();
      fetchReplyRules();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to save reply rule', 'error');
    }
  };

  // Flex Template Dialog Handlers
  const handleOpenFlexTemplateDialog = (mode: 'create' | 'edit', template?: FlexTemplate) => {
    if (mode === 'edit' && template) {
      setFlexTemplateForm({
        name: template.name,
        description: template.description || '',
        category: template.category,
        flex_content: JSON.stringify(template.flex_content, null, 2),
        variables: template.variables.join(', '),
        is_active: template.is_active,
      });
      setFlexTemplateDialog({ open: true, mode, data: template });
    } else {
      setFlexTemplateForm({
        name: '',
        description: '',
        category: 'general',
        flex_content: '{\n  "type": "bubble",\n  "body": {\n    "type": "box",\n    "layout": "vertical",\n    "contents": []\n  }\n}',
        variables: '',
        is_active: true,
      });
      setFlexTemplateDialog({ open: true, mode, data: null });
    }
  };

  const handleCloseFlexTemplateDialog = () => {
    setFlexTemplateDialog({ open: false, mode: 'create', data: null });
    setFlexTemplateForm({
      name: '',
      description: '',
      category: 'general',
      flex_content: '',
      variables: '',
      is_active: true,
    });
  };

  const handleSaveFlexTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      let parsedContent;
      try {
        parsedContent = JSON.parse(flexTemplateForm.flex_content);
      } catch (e) {
        showSnackbar('Invalid JSON format for Flex content', 'error');
        return;
      }
      
      const payload = {
        name: flexTemplateForm.name,
        description: flexTemplateForm.description || undefined,
        category: flexTemplateForm.category,
        flex_content: parsedContent,
        variables: flexTemplateForm.variables.split(',').map(v => v.trim()).filter(v => v),
        is_active: flexTemplateForm.is_active,
      };
      
      if (flexTemplateDialog.mode === 'create') {
        await axios.post(`${API_URL}/api/v1/line/flex-templates`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar('Flex template created successfully', 'success');
      } else if (flexTemplateDialog.data) {
        await axios.put(`${API_URL}/api/v1/line/flex-templates/${flexTemplateDialog.data._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar('Flex template updated successfully', 'success');
      }
      
      handleCloseFlexTemplateDialog();
      fetchFlexTemplates();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to save flex template', 'error');
    }
  };

  const handleOpenLiffDialog = (mode: 'create' | 'edit', app?: LiffApp) => {
    if (mode === 'edit' && app) {
      setLiffForm({
        liff_id: app.liff_id,
        app_name: app.app_name,
        app_key: app.app_key,
        description: app.description || '',
        endpoint_url: app.endpoint_url,
        is_active: app.is_active,
      });
      setLiffDialog({ open: true, mode, data: app });
    } else {
      setLiffForm({
        liff_id: '',
        app_name: '',
        app_key: '',
        description: '',
        endpoint_url: '',
        is_active: true,
      });
      setLiffDialog({ open: true, mode, data: null });
    }
  };

  const handleCloseLiffDialog = () => {
    setLiffDialog({ open: false, mode: 'create', data: null });
    setLiffForm({
      liff_id: '',
      app_name: '',
      app_key: '',
      description: '',
      endpoint_url: '',
      is_active: true,
    });
  };

  const handleSaveLiffApp = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (liffDialog.mode === 'create') {
        await axios.post(`${API_URL}/api/v1/liff/liff-apps`, liffForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar('LIFF app created successfully', 'success');
      } else if (liffDialog.data) {
        await axios.put(`${API_URL}/api/v1/liff/liff-apps/${liffDialog.data._id}`, liffForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar('LIFF app updated successfully', 'success');
      }
      
      handleCloseLiffDialog();
      fetchLiffApps();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to save LIFF app', 'error');
    }
  };

  const handleDeleteLiffApp = async (appId: string) => {
    if (!confirm('Are you sure you want to delete this LIFF app?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/v1/liff/liff-apps/${appId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSnackbar('LIFF app deleted successfully', 'success');
      fetchLiffApps();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to delete LIFF app', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        LINE Bot Management
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage LINE Bot credentials, settings, reply rules, and flex message templates
      </Typography>

      <Card>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Credentials & Config" />
          <Tab label="Bot Settings" />
          <Tab label="LIFF Apps" />
          <Tab label="Reply Rules" />
          <Tab label="Flex Templates" />
          <Tab label="Rich Menu Manager" />
          <Tab label="Broadcast Messages" />
          <Tab label="User Management" />
        </Tabs>

        <Divider />

        {/* Tab 0: Credentials & Config */}
        <TabPanel value={tabValue} index={0}>
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Configure your LINE Bot credentials. These will be stored securely in the database and override environment variables.
                </Alert>
              </Grid>

            {/* API Status Monitoring */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="h6" gutterBottom>
                  🔌 Real-time API Status
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {apiStatus.connectionStatus === 'connected' && <CheckCircle color="success" />}
                      {apiStatus.connectionStatus === 'disconnected' && <Chip label="Disconnected" color="error" size="small" />}
                      {apiStatus.connectionStatus === 'checking' && <CircularProgress size={20} />}
                      {!apiStatus.connectionStatus && <Chip label="Not Checked" size="small" />}
                      <Typography variant="body2">
                        Connection Status: {apiStatus.connectionStatus || 'Unknown'}
                      </Typography>
                    </Box>
                    
                    {apiStatus.profile && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2"><strong>Bot Name:</strong> {apiStatus.profile.displayName}</Typography>
                        <Typography variant="body2"><strong>User ID:</strong> {apiStatus.profile.userId}</Typography>
                        <Typography variant="body2"><strong>Status Message:</strong> {apiStatus.profile.statusMessage || 'None'}</Typography>
                      </Box>
                    )}
                    
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={fetchBotProfile}
                      disabled={apiStatus.loading}
                      sx={{ mt: 2 }}
                    >
                      {apiStatus.loading ? 'Fetching...' : 'Fetch Bot Profile'}
                    </Button>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" gutterBottom><strong>📊 Message Quota</strong></Typography>
                    {apiStatus.quota ? (
                      <Box>
                        <Typography variant="body2">
                          Used: {apiStatus.quota.consumption?.totalUsage || 0} / {apiStatus.quota.quota?.value || 'Unlimited'}
                        </Typography>
                        {apiStatus.quota.quota?.value && (
                          <Typography variant="body2" color="text.secondary">
                            {((apiStatus.quota.consumption?.totalUsage / apiStatus.quota.quota.value) * 100).toFixed(1)}% used
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No quota data</Typography>
                    )}
                    
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={fetchMessageQuota}
                      disabled={apiStatus.loading}
                      sx={{ mt: 2 }}
                    >
                      {apiStatus.loading ? 'Fetching...' : 'Fetch Message Quota'}
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* Follower Statistics */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="h6" gutterBottom>
                  👥 Follower Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Total Followers</Typography>
                    <Typography variant="h4">
                      {loadingFollowers ? (
                        <CircularProgress size={24} />
                      ) : (
                        followerStats?.followers?.toLocaleString() || '0'
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Target Reaches</Typography>
                    <Typography variant="h4">
                      {loadingFollowers ? (
                        <CircularProgress size={24} />
                      ) : (
                        followerStats?.targetReaches?.toLocaleString() || '0'
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip 
                      label={followerStats?.status || 'Unknown'}
                      color={followerStats?.status === 'ready' ? 'success' : 'default'}
                      sx={{ mt: 1 }}
                    />
                    <br />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={fetchFollowerStats}
                      disabled={loadingFollowers}
                      sx={{ mt: 2 }}
                    >
                      {loadingFollowers ? 'Fetching...' : 'Refresh Stats'}
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                LINE Messaging API
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Channel ID"
                value={settings.line_channel_id || ''}
                onChange={(e) => setSettings({ ...settings, line_channel_id: e.target.value })}
                placeholder="1234567890"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Channel Secret"
                type={showSecrets.channelSecret ? 'text' : 'password'}
                value={settings.line_channel_secret || ''}
                onChange={(e) => setSettings({ ...settings, line_channel_secret: e.target.value })}
                placeholder="Enter channel secret"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowSecrets({ ...showSecrets, channelSecret: !showSecrets.channelSecret })}
                        edge="end"
                      >
                        {showSecrets.channelSecret ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Channel Access Token"
                type={showSecrets.accessToken ? 'text' : 'password'}
                value={settings.line_channel_access_token || ''}
                onChange={(e) => setSettings({ ...settings, line_channel_access_token: e.target.value })}
                placeholder="Enter channel access token (long-lived)"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowSecrets({ ...showSecrets, accessToken: !showSecrets.accessToken })}
                        edge="end"
                      >
                        {showSecrets.accessToken ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                LINE Login
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Login Channel ID"
                value={settings.line_login_channel_id || ''}
                onChange={(e) => setSettings({ ...settings, line_login_channel_id: e.target.value })}
                placeholder="1234567890"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Login Channel Secret"
                type={showSecrets.loginSecret ? 'text' : 'password'}
                value={settings.line_login_channel_secret || ''}
                onChange={(e) => setSettings({ ...settings, line_login_channel_secret: e.target.value })}
                placeholder="Enter login channel secret"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowSecrets({ ...showSecrets, loginSecret: !showSecrets.loginSecret })}
                        edge="end"
                      >
                        {showSecrets.loginSecret ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                LIFF (LINE Front-end Framework)
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="LIFF ID"
                value={settings.liff_id || ''}
                onChange={(e) => setSettings({ ...settings, liff_id: e.target.value })}
                placeholder="1234567890-abcdefgh"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Webhook Configuration
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Webhook URL"
                value={settings.webhook_url || `${API_URL}/api/v1/line/webhook`}
                onChange={(e) => setSettings({ ...settings, webhook_url: e.target.value })}
                placeholder={`${API_URL}/api/v1/line/webhook`}
                helperText={`Default: ${API_URL}/api/v1/line/webhook - Register this in LINE Developers Console`}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="LINE Login Callback URL"
                value={settings.line_login_callback_url || `${API_URL}/api/v1/line/auth/callback`}
                onChange={(e) => setSettings({ ...settings, line_login_callback_url: e.target.value })}
                placeholder={`${API_URL}/api/v1/line/auth/callback`}
                helperText={`Default: ${API_URL}/api/v1/line/auth/callback - Configure this in LINE Developers Console`}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.webhook_enabled}
                    onChange={(e) => setSettings({ ...settings, webhook_enabled: e.target.checked })}
                  />
                }
                label="Enable Webhook Processing"
              />
            </Grid>

            {/* Telegram Integration */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                📱 Telegram Integration
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Configure Telegram bot for system alerts and notifications
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telegram Bot Token"
                type={showSecrets.channelSecret ? 'text' : 'password'}
                value={settings.telegram_bot_token || ''}
                onChange={(e) => setSettings({ ...settings, telegram_bot_token: e.target.value })}
                placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowSecrets({ ...showSecrets, channelSecret: !showSecrets.channelSecret })}
                        edge="end"
                      >
                        {showSecrets.channelSecret ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Admin Chat ID"
                value={settings.telegram_admin_chat_id || ''}
                onChange={(e) => setSettings({ ...settings, telegram_admin_chat_id: e.target.value })}
                placeholder="123456789"
                helperText="Your Telegram user/chat ID for receiving alerts"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.telegram_alerts_enabled || false}
                    onChange={(e) => setSettings({ ...settings, telegram_alerts_enabled: e.target.checked })}
                  />
                }
                label="Enable Telegram System Alerts"
              />
            </Grid>

            {/* Interactive Configuration Buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                ⚙️ Quick Configuration
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={configureWebhook}
                  disabled={saving}
                >
                  Configure Webhook
                </Button>
                
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={fetchWebhookInfo}
                  disabled={apiStatus.loading}
                >
                  Fetch Webhook Info
                </Button>
                
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={configureLoginCallback}
                >
                  Configure Login Callback
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={fetchBotProfile}
                  disabled={apiStatus.loading}
                >
                  Fetch Bot Profile & Status
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={fetchMessageQuota}
                  disabled={apiStatus.loading}
                >
                  Fetch Message Quota
                </Button>
              </Stack>
            </Grid>
          </Grid>
          </Box>
        </TabPanel>

        {/* Tab 1: Bot Settings */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Settings
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bot Name"
                value={settings.bot_name}
                onChange={(e) => setSettings({ ...settings, bot_name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Welcome Message"
                value={settings.welcome_message}
                onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })}
                helperText="Message sent when user adds bot as friend"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Fallback Message"
                value={settings.fallback_message}
                onChange={(e) => setSettings({ ...settings, fallback_message: e.target.value })}
                helperText="Message sent when no reply rule matches"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Features
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.auto_reply_enabled}
                    onChange={(e) => setSettings({ ...settings, auto_reply_enabled: e.target.checked })}
                  />
                }
                label="Enable Auto Reply"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Automatically reply to messages based on reply rules
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.flex_messages_enabled}
                    onChange={(e) => setSettings({ ...settings, flex_messages_enabled: e.target.checked })}
                  />
                }
                label="Enable Flex Messages"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Allow bot to send rich Flex Message templates
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.behavior_tracking_enabled}
                    onChange={(e) => setSettings({ ...settings, behavior_tracking_enabled: e.target.checked })}
                  />
                }
                label="Enable Behavior Tracking"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Track user interactions for analytics and personalization
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Rich Menu
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rich Menu ID"
                value={settings.rich_menu_id || ''}
                onChange={(e) => setSettings({ ...settings, rich_menu_id: e.target.value })}
                placeholder="richmenu-xxxxxxxxxxxxx"
                helperText="Optional: Rich Menu ID from LINE Developers Console"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 2: LIFF Apps */}
        <TabPanel value={tabValue} index={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              LIFF Applications ({liffApps.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenLiffDialog('create')}
            >
              Add LIFF App
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>App Name</TableCell>
                  <TableCell>App Key</TableCell>
                  <TableCell>LIFF ID</TableCell>
                  <TableCell>Endpoint URL</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {liffApps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        No LIFF apps yet. Click "Add LIFF App" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  liffApps.map((app) => (
                    <TableRow key={app._id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {app.app_name}
                        </Typography>
                        {app.description && (
                          <Typography variant="caption" color="text.secondary">
                            {app.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={app.app_key} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">
                          {app.liff_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                          {app.endpoint_url}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {app.is_active ? (
                          <Chip label="Active" color="success" size="small" icon={<CheckCircle />} />
                        ) : (
                          <Chip label="Inactive" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenLiffDialog('edit', app)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteLiffApp(app._id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab 3: Reply Rules */}
        <TabPanel value={tabValue} index={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              Reply Rules ({replyRules.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenReplyRuleDialog('create')}
            >
              Add Rule
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Keywords</TableCell>
                  <TableCell>Match Type</TableCell>
                  <TableCell>Reply Type</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Usage</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {replyRules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary">
                        No reply rules yet. Click "Add Rule" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  replyRules.map((rule) => (
                    <TableRow key={rule._id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {rule.name}
                        </Typography>
                        {rule.description && (
                          <Typography variant="caption" color="text.secondary">
                            {rule.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {rule.keywords.slice(0, 3).map((keyword, i) => (
                            <Chip key={i} label={keyword} size="small" />
                          ))}
                          {rule.keywords.length > 3 && (
                            <Chip label={`+${rule.keywords.length - 3}`} size="small" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={rule.match_type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={rule.reply_type} size="small" color="primary" />
                      </TableCell>
                      <TableCell>{rule.priority}</TableCell>
                      <TableCell>{rule.usage_count}</TableCell>
                      <TableCell>
                        {rule.is_active ? (
                          <Chip label="Active" color="success" size="small" icon={<CheckCircle />} />
                        ) : (
                          <Chip label="Inactive" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenReplyRuleDialog('edit', rule)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteRule(rule._id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab 4: Flex Templates */}
        <TabPanel value={tabValue} index={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              Flex Message Templates ({flexTemplates.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenFlexTemplateDialog('create')}
            >
              Add Template
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Variables</TableCell>
                  <TableCell>Usage</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flexTemplates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        No flex templates yet. Click "Add Template" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  flexTemplates.map((template) => (
                    <TableRow key={template._id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {template.name}
                        </Typography>
                        {template.description && (
                          <Typography variant="caption" color="text.secondary">
                            {template.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={template.category} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {template.variables.slice(0, 3).map((variable, i) => (
                            <Chip key={i} label={variable} size="small" />
                          ))}
                          {template.variables.length > 3 && (
                            <Chip label={`+${template.variables.length - 3}`} size="small" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{template.usage_count}</TableCell>
                      <TableCell>
                        {template.is_active ? (
                          <Chip label="Active" color="success" size="small" icon={<CheckCircle />} />
                        ) : (
                          <Chip label="Inactive" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenFlexTemplateDialog('edit', template)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTemplate(template._id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab 5: Rich Menu Management */}
        <TabPanel value={tabValue} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  🎨 Rich Menu Manager
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={fetchRichMenus}
                    disabled={loadingRichMenus}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      setRichMenuDialog({ open: true, mode: 'create', data: null });
                    }}
                  >
                    Create Rich Menu
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              {richMenus.length > 0 && (
                <Box mb={2}>
                  <Alert severity="info">
                    Showing {richMenus.length} rich menu(s). This includes menus from LINE API and local database.
                  </Alert>
                </Box>
              )}
              
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Alias</TableCell>
                      <TableCell>Chat Bar Text</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Areas</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingRichMenus ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : richMenus.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary">
                            No rich menus yet. Click "Create Rich Menu" to add one.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      richMenus.map((menu) => (
                        <TableRow key={menu._id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {menu.name}
                            </Typography>
                            {menu.description && (
                              <Typography variant="caption" color="text.secondary">
                                {menu.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{menu.alias || '-'}</TableCell>
                          <TableCell>{menu.chat_bar_text}</TableCell>
                          <TableCell>
                            {menu.size.width} × {menu.size.height}
                          </TableCell>
                          <TableCell>{menu.areas.length} areas</TableCell>
                          <TableCell>
                            {menu.rich_menu_id ? (
                              <Chip label="Published" color="success" size="small" icon={<CheckCircle />} />
                            ) : (
                              <Chip label="Draft" size="small" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {!menu.rich_menu_id && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handlePublishRichMenu(menu._id)}
                                sx={{ mr: 1 }}
                              >
                                Publish
                              </Button>
                            )}
                            {menu.rich_menu_id && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSetDefaultRichMenu(menu._id)}
                                sx={{ mr: 1 }}
                              >
                                Set Default
                              </Button>
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteRichMenu(menu._id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 6: Broadcast Messages */}
        <TabPanel value={tabValue} index={6}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                ⚠️ Broadcast messages will be sent to ALL followers. Use with caution!
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  📢 Send Broadcast Message
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Send a message to all LINE followers at once
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setBroadcastDialog(true)}
                >
                  Compose Broadcast
                </Button>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  📊 Broadcast Guidelines
                </Typography>
                <Box component="ul">
                  <li>Messages are sent immediately to all followers</li>
                  <li>Supports text messages and Flex Messages</li>
                  <li>Check follower count before sending</li>
                  <li>Message delivery depends on LINE's quota limits</li>
                  <li>All broadcasts are logged for audit purposes</li>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 7: User Management */}
        <TabPanel value={tabValue} index={7}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                👥 User & Follower Analytics
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h3" color="primary">
                  {loadingFollowers ? (
                    <CircularProgress />
                  ) : (
                    followerStats?.followers?.toLocaleString() || '0'
                  )}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Total Followers
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h3" color="success.main">
                  {loadingFollowers ? (
                    <CircularProgress />
                  ) : (
                    followerStats?.targetReaches?.toLocaleString() || '0'
                  )}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Target Reaches
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Chip 
                  label={followerStats?.status || 'Unknown'}
                  color={followerStats?.status === 'ready' ? 'success' : 'default'}
                  size="medium"
                  sx={{ fontSize: '1.2rem', py: 2, px: 3 }}
                />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  API Status
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={fetchFollowerStats}
                disabled={loadingFollowers}
              >
                {loadingFollowers ? 'Refreshing...' : 'Refresh Statistics'}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  📈 Insights
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {followerStats?.date && `Last updated: ${new Date(followerStats.date).toLocaleString()}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Data is fetched from LINE Insight API and represents the latest available statistics.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <Divider />

        <Box p={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* LIFF App Dialog */}
      <Dialog 
        open={liffDialog.open} 
        onClose={handleCloseLiffDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {liffDialog.mode === 'create' ? 'Create LIFF App' : 'Edit LIFF App'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="App Name"
                value={liffForm.app_name}
                onChange={(e) => setLiffForm({ ...liffForm, app_name: e.target.value })}
                placeholder="e.g., Dashboard, QR Scanner"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="App Key"
                value={liffForm.app_key}
                onChange={(e) => setLiffForm({ ...liffForm, app_key: e.target.value })}
                placeholder="e.g., dashboard, qr_scanner"
                helperText="Unique identifier (alphanumeric and underscore)"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="LIFF ID"
                value={liffForm.liff_id}
                onChange={(e) => setLiffForm({ ...liffForm, liff_id: e.target.value })}
                placeholder="e.g., 1234567890-abcdefgh"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Endpoint URL"
                value={liffForm.endpoint_url}
                onChange={(e) => setLiffForm({ ...liffForm, endpoint_url: e.target.value })}
                placeholder="https://liff.example.com/dashboard"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={liffForm.description}
                onChange={(e) => setLiffForm({ ...liffForm, description: e.target.value })}
                placeholder="Optional description"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={liffForm.is_active}
                    onChange={(e) => setLiffForm({ ...liffForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLiffDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveLiffApp}
            disabled={!liffForm.liff_id || !liffForm.app_name || !liffForm.app_key || !liffForm.endpoint_url}
          >
            {liffDialog.mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast Message Dialog */}
      <Dialog 
        open={broadcastDialog} 
        onClose={() => setBroadcastDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>📢 Compose Broadcast Message</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This message will be sent to ALL {followerStats?.followers?.toLocaleString() || '0'} followers immediately!
          </Alert>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={broadcastForm.message_type === 'flex'}
                    onChange={(e) => setBroadcastForm({ 
                      ...broadcastForm, 
                      message_type: e.target.checked ? 'flex' : 'text' 
                    })}
                  />
                }
                label="Use Flex Message (Advanced)"
              />
            </Grid>

            {broadcastForm.message_type === 'text' ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Message Text"
                  value={broadcastForm.text}
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, text: e.target.value })}
                  placeholder="Enter your broadcast message..."
                  required
                  helperText={`${broadcastForm.text.length} characters`}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  label="Flex Message JSON"
                  value={broadcastForm.flex_content}
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, flex_content: e.target.value })}
                  placeholder='{"type": "bubble", "body": {...}}'
                  required
                  helperText="Enter valid Flex Message JSON format"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBroadcastDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSendBroadcast}
            disabled={
              sendingBroadcast || 
              (broadcastForm.message_type === 'text' && !broadcastForm.text) ||
              (broadcastForm.message_type === 'flex' && !broadcastForm.flex_content)
            }
          >
            {sendingBroadcast ? 'Sending...' : 'Send Broadcast'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reply Rule Dialog */}
      <Dialog 
        open={replyRuleDialog.open} 
        onClose={handleCloseReplyRuleDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {replyRuleDialog.mode === 'create' ? 'Create Reply Rule' : 'Edit Reply Rule'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Rule Name"
                value={replyRuleForm.name}
                onChange={(e) => setReplyRuleForm({ ...replyRuleForm, name: e.target.value })}
                placeholder="e.g., Welcome Greeting"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Priority"
                value={replyRuleForm.priority}
                onChange={(e) => setReplyRuleForm({ ...replyRuleForm, priority: parseInt(e.target.value) || 0 })}
                helperText="Higher priority rules are checked first"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={replyRuleForm.description}
                onChange={(e) => setReplyRuleForm({ ...replyRuleForm, description: e.target.value })}
                placeholder="Optional description"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={replyRuleForm.keywords}
                onChange={(e) => setReplyRuleForm({ ...replyRuleForm, keywords: e.target.value })}
                placeholder="hello, hi, สวัสดี"
                helperText="Comma-separated keywords that trigger this rule"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Match Type</InputLabel>
                <Select
                  value={replyRuleForm.match_type}
                  label="Match Type"
                  onChange={(e) => setReplyRuleForm({ ...replyRuleForm, match_type: e.target.value })}
                >
                  <MenuItem value="exact">Exact Match</MenuItem>
                  <MenuItem value="contains">Contains</MenuItem>
                  <MenuItem value="starts_with">Starts With</MenuItem>
                  <MenuItem value="ends_with">Ends With</MenuItem>
                  <MenuItem value="regex">Regex Pattern</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Reply Type</InputLabel>
                <Select
                  value={replyRuleForm.reply_type}
                  label="Reply Type"
                  onChange={(e) => setReplyRuleForm({ ...replyRuleForm, reply_type: e.target.value })}
                >
                  <MenuItem value="text">Text Message</MenuItem>
                  <MenuItem value="flex">Flex Message</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {replyRuleForm.match_type === 'regex' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Regex Pattern"
                  value={replyRuleForm.pattern}
                  onChange={(e) => setReplyRuleForm({ ...replyRuleForm, pattern: e.target.value })}
                  placeholder="e.g., ^(hello|hi).*"
                  helperText="Regular expression pattern"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={replyRuleForm.reply_type === 'flex' ? 8 : 4}
                label="Reply Content"
                value={replyRuleForm.reply_content}
                onChange={(e) => setReplyRuleForm({ ...replyRuleForm, reply_content: e.target.value })}
                placeholder={replyRuleForm.reply_type === 'flex' ? '{"type": "bubble", ...}' : 'Enter reply message...'}
                helperText={replyRuleForm.reply_type === 'flex' ? 'Enter valid Flex Message JSON' : 'Text message to reply with'}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={replyRuleForm.is_active}
                    onChange={(e) => setReplyRuleForm({ ...replyRuleForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReplyRuleDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveReplyRule}
            disabled={!replyRuleForm.name || !replyRuleForm.keywords || !replyRuleForm.reply_content}
          >
            {replyRuleDialog.mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Flex Template Dialog */}
      <Dialog 
        open={flexTemplateDialog.open} 
        onClose={handleCloseFlexTemplateDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {flexTemplateDialog.mode === 'create' ? 'Create Flex Template' : 'Edit Flex Template'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Template Name"
                value={flexTemplateForm.name}
                onChange={(e) => setFlexTemplateForm({ ...flexTemplateForm, name: e.target.value })}
                placeholder="e.g., Welcome Card"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={flexTemplateForm.category}
                  label="Category"
                  onChange={(e) => setFlexTemplateForm({ ...flexTemplateForm, category: e.target.value })}
                >
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="welcome">Welcome</MenuItem>
                  <MenuItem value="notification">Notification</MenuItem>
                  <MenuItem value="promotion">Promotion</MenuItem>
                  <MenuItem value="information">Information</MenuItem>
                  <MenuItem value="event">Event</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={flexTemplateForm.description}
                onChange={(e) => setFlexTemplateForm({ ...flexTemplateForm, description: e.target.value })}
                placeholder="Optional description"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Variables"
                value={flexTemplateForm.variables}
                onChange={(e) => setFlexTemplateForm({ ...flexTemplateForm, variables: e.target.value })}
                placeholder="name, date, amount"
                helperText="Comma-separated variable names that can be replaced in the template"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={15}
                label="Flex Message JSON"
                value={flexTemplateForm.flex_content}
                onChange={(e) => setFlexTemplateForm({ ...flexTemplateForm, flex_content: e.target.value })}
                placeholder='{"type": "bubble", "body": {...}}'
                helperText="Enter valid Flex Message JSON format. Use {{variable}} for dynamic content."
                required
                sx={{ fontFamily: 'monospace' }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={flexTemplateForm.is_active}
                    onChange={(e) => setFlexTemplateForm({ ...flexTemplateForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFlexTemplateDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveFlexTemplate}
            disabled={!flexTemplateForm.name || !flexTemplateForm.flex_content}
          >
            {flexTemplateDialog.mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rich Menu Dialog */}
      <RichMenuEditor
        open={richMenuDialog.open}
        mode={richMenuDialog.mode}
        initialData={richMenuDialog.mode === 'edit' ? richMenuDialog.data : undefined}
        onClose={() => {
          setRichMenuDialog({ open: false, mode: 'create', data: null });
        }}
        onSave={async (data, imageFile) => {
          try {
            const token = localStorage.getItem('token');
            if (richMenuDialog.mode === 'create') {
              const response = await axios.post(`${API_URL}/api/v1/rich-menus`, data, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const richMenuId = response.data.rich_menu_id;

              if (imageFile) {
                const formData = new FormData();
                formData.append('image', imageFile);
                await axios.post(`${API_URL}/api/v1/rich-menus/${richMenuId}/upload-image`, formData, {
                  headers: { 
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                  }
                });
              }

              showSnackbar('Rich menu created successfully', 'success');
              fetchRichMenus();
              setRichMenuDialog({ open: false, mode: 'create', data: null });
            }
          } catch (error: any) {
            showSnackbar(error.response?.data?.detail || 'Failed to save rich menu', 'error');
            throw error;
          }
        }}
        onShowSnackbar={showSnackbar}
      />
    </Box>
  );
}

export default LineBotManagement;
